import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  BRAND,
  contrastText,
  getThemeColors,
  cardStyle,
  sectionHeaderStyle,
  sendBtnStyle,
} from "./themes";
import { API_BASE_URL } from "./config";
import { useIsMobile, ThemeToggle } from "./App";

export default function TranscriptPage({ theme, setTheme }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const themeColors = getThemeColors(theme);
  const targetSessionId = location.state?.session_id || null;

  const [sessions, setSessions] = useState(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/transcripts`, {
          method: "GET",
          credentials: "include",
        });
        if (res.status === 401) { navigate("/login", { replace: true }); return; }
        if (!res.ok) return;
        const data = await res.json();
        const map = new Map();
        for (const row of data.transcripts || []) {
          if (!map.has(row.session_id)) {
            map.set(row.session_id, {
              session_id: row.session_id,
              scenario_name: row.scenario_name,
              timestamp: row.timestamp,
              evaluation: row.evaluation || null,
              turns: [],
            });
          }
          map.get(row.session_id).turns.push(row);
        }
        setSessions(map);
      } catch (err) {
        console.error("Error fetching transcripts:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const session = targetSessionId ? sessions.get(targetSessionId) : null;

const handleExport = async () => {
  const res = await fetch(`${API_BASE_URL}/export`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      source: "transcript_page",
      scenarioName: session.scenario_name,
      timestamp: `${new Date(session.timestamp).toLocaleDateString()} ${new Date(session.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`,
      turns: session.turns,
      evaluation: session.evaluation || "",
    }),
  });
  const data = await res.json();
  const blob = new Blob([data.content], { type: 'text/plain' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${session.scenario_name}_${session.session_id}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
  return (
    <div style={{
      padding: isMobile ? 12 : 28,
      background: themeColors.background,
      minHeight: '100vh',
      fontFamily: BRAND.font,
    }}>
      <div style={{ maxWidth: '100%' }}>
        {/* Nav row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <button
            onClick={() => navigate("/userdashboard")}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              background: themeColors.primary,
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            ← Back
          </button>
          <button
            onClick={handleExport}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              background: themeColors.primary,
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Export
          </button>
          <ThemeToggle theme={theme} setTheme={setTheme} />
        </div>

        {loading ? (
          <p style={{ color: themeColors.textMuted, fontSize: 14 }}>Loading…</p>
        ) : !session ? (
          <p style={{ color: themeColors.textMuted, fontSize: 14 }}>Transcript not found.</p>
        ) : (
          <div style={cardStyle(themeColors)}>
            <div style={sectionHeaderStyle(themeColors)}>{session.scenario_name}</div>
            <div style={{ fontSize: 12, color: themeColors.textMuted, marginBottom: 16 }}>
              {new Date(session.timestamp).toLocaleDateString()}  {new Date(session.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })} · {session.turns.length} turns
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {session.turns.map((t) => (
                <div key={t.id} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: themeColors.text, marginBottom: 4 }}>
                    {t.role}:
                  </div>
                  <div style={{ fontSize: 14, lineHeight: '20px', color: themeColors.text, whiteSpace: 'pre-line' }}>
                    {t.content}
                  </div>
                </div>
              ))}
            </div>
            {session.evaluation && (
                      <div style={{ marginTop: 24 }}>
                        <div style={sectionHeaderStyle(themeColors)}>Evaluation</div>
                        <div style={{ fontSize: 14, lineHeight: '20px', color: themeColors.text, whiteSpace: 'pre-line' }}>
                          {session.evaluation}
                        </div>
                      </div>
            )}
            <button
              onClick={async () => {
                const res = await fetch(`${API_BASE_URL}/continue`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  credentials: "include",
                  body: JSON.stringify({ session_id: session.session_id }),
                });
                const data = await res.json();
                const route = data.mode === "mentoring" ? "/mentoring-app" : "/app";
                navigate(route, {
                  state: {
                    continuation: {
                      sessionId: data.session_id,
                      character: data.character,
                      background: data.background,
                      scenario: data.scenario,
                      mode: data.mode,
                      turns: data.turns,
                    }
                  }
                });
              }}
            >
              Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
}